import { GOALS } from '@/lib/goals';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Copy, Dumbbell, Moon, StickyNote, X, ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import { TemplateVersionHistory } from '@/components/TemplateVersionHistory';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const BLUE = '#00C8FF';

const newDay = (i) => ({ day_index: i, items: [] });
const newWeek = (num) => ({ week_number: num, days: Array.from({ length: 7 }, (_, i) => newDay(i)) });
const normalizeWeeks = (weeks) => (weeks || []).map((w, wi) => {
  const byIdx = {};
  (w.days || []).forEach(d => { byIdx[d.day_index] = d; });
  return { week_number: wi + 1, days: Array.from({ length: 7 }, (_, i) => byIdx[i] ? { day_index: i, items: byIdx[i].items || [] } : newDay(i)) };
});

export default function TrainingPrograms() {
  const [programs, setPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState({ goal: '', weeks: 4, days_per_week: 4, experience: 'beginner', equipment: 'full gym', notes: '', fitness_racing: false });

  const load = async () => {
    const [p, w] = await Promise.all([api.get('/training-programs'), api.get('/workouts')]);
    setPrograms(p.data); setWorkouts(w.data);
  };
  useEffect(() => { load(); }, []);

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post('/training-programs/ai-generate', ai);
      toast.success('AI program drafted — review & edit below');
      await load();
      setAiOpen(false);
      openBuilder(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const openBuilder = (prog) => {
    if (prog) {
      setEditing(prog.id);
      setForm({ ...prog, weeks: normalizeWeeks(prog.weeks.length ? prog.weeks : [newWeek(1)]) });
    } else {
      setEditing(null);
      setForm({ name: '', goal: '', description: '', duration_weeks: 4, weeks: [newWeek(1)], is_template: true });
    }
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Program name required');
    const payload = { ...form, duration_weeks: form.weeks.length };
    try {
      if (editing) await api.put(`/training-programs/${editing}`, payload);
      else await api.post('/training-programs', payload);
      toast.success('Program saved'); setOpen(false); load();
    } catch { toast.error('Failed to save'); }
  };

  const duplicate = async (id) => { await api.post(`/training-programs/${id}/duplicate`); toast.success('Duplicated'); load(); };
  const del = async (id) => { if (!confirm('Delete this program?')) return; await api.delete(`/training-programs/${id}`); toast.success('Deleted'); load(); };

  // ---- builder mutations ----
  const setWeeks = (weeks) => setForm(f => ({ ...f, weeks }));
  const addWeek = () => setWeeks([...form.weeks, newWeek(form.weeks.length + 1)]);
  const dupWeek = (wi) => {
    const copy = JSON.parse(JSON.stringify(form.weeks[wi]));
    const weeks = [...form.weeks]; weeks.splice(wi + 1, 0, copy);
    setWeeks(weeks.map((w, i) => ({ ...w, week_number: i + 1 })));
  };
  const delWeek = (wi) => {
    if (form.weeks.length === 1) return toast.error('Need at least one week');
    setWeeks(form.weeks.filter((_, i) => i !== wi).map((w, i) => ({ ...w, week_number: i + 1 })));
  };
  const dupDayToNext = (wi, di) => {
    if (di >= 6) return toast.error('No next day in week');
    const weeks = JSON.parse(JSON.stringify(form.weeks));
    weeks[wi].days[di + 1].items = JSON.parse(JSON.stringify(weeks[wi].days[di].items));
    setWeeks(weeks);
  };
  const clearDay = (wi, di) => {
    const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].items = []; setWeeks(weeks);
  };
  const addItem = (wi, di, item) => {
    const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].items.push(item); setWeeks(weeks);
  };
  const updItem = (wi, di, ii, k, v) => {
    const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].items[ii][k] = v; setWeeks(weeks);
  };
  const delItem = (wi, di, ii) => {
    const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].items.splice(ii, 1); setWeeks(weeks);
  };

  return (
    <div className="space-y-4" data-testid="training-programs">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground max-w-xl">Build multi-week training programs from your workouts, then assign them to clients from their profile.</p>
        <div className="flex gap-2">
          <Button onClick={() => setAiOpen(true)} data-testid="ai-generate-program-btn" variant="outline" className="font-semibold border-[#00C8FF]/40 text-[#00C8FF]"><Sparkles className="w-4 h-4 mr-2" /> Generate with AI</Button>
          <Button onClick={() => openBuilder(null)} data-testid="add-program-btn" className="font-semibold" style={{ background: BLUE, color: '#081726' }}><Plus className="w-4 h-4 mr-2" /> New Program</Button>
        </div>
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#00C8FF]" /> AI Training Program Generator</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">AI drafts a full program using your existing workouts and creates new ones from your exercise library when needed. You review & edit before assigning.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Goal</Label>
                <Select value={ai.goal} onValueChange={v => setAi({ ...ai, goal: v })}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="ai-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
                  <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Experience</Label>
                <Select value={ai.experience} onValueChange={v => setAi({ ...ai, experience: v })}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{['beginner', 'intermediate', 'advanced'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Weeks</Label><Input type="number" min="1" max="16" value={ai.weeks} onChange={e => setAi({ ...ai, weeks: parseInt(e.target.value) || 4 })} data-testid="ai-weeks" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Days / week</Label><Input type="number" min="1" max="7" value={ai.days_per_week} onChange={e => setAi({ ...ai, days_per_week: parseInt(e.target.value) || 4 })} data-testid="ai-days" className="bg-transparent border-[var(--c-hair)]" /></div>
            </div>
            <div><Label>Equipment</Label><Input value={ai.equipment} onChange={e => setAi({ ...ai, equipment: e.target.value })} placeholder="full gym / home / bodyweight" className="bg-transparent border-[var(--c-hair)]" /></div>
            <div><Label>Notes (optional)</Label><Textarea value={ai.notes} onChange={e => setAi({ ...ai, notes: e.target.value })} rows={2} placeholder="injuries, preferences, focus areas…" className="bg-transparent border-[var(--c-hair)]" /></div>
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[var(--c-hair)] cursor-pointer" data-testid="ai-fitness-racing-label">
              <input type="checkbox" checked={!!ai.fitness_racing} onChange={e => setAi({ ...ai, fitness_racing: e.target.checked })} data-testid="ai-fitness-racing" className="mt-0.5 w-4 h-4 accent-[#00C8FF]" />
              <span className="text-sm">Include a Fitness Racing circuit each week
                <span className="block text-[11px] text-muted-foreground mt-0.5">Selects an existing Coach JP Fitness Racing circuit (never AI-created). Tick this when the client opted into Fitness Racing.</span>
              </span>
            </label>
            <Button onClick={generateAI} disabled={aiLoading} data-testid="ai-generate-submit" className="w-full font-semibold" style={{ background: BLUE, color: '#081726' }}>
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating… (~20s)</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Program</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.length === 0 && <div className="text-sm text-muted-foreground col-span-full py-10 text-center">No programs yet. Create your first multi-week program.</div>}
        {programs.map(p => {
          const totalWorkouts = (p.weeks || []).reduce((s, w) => s + (w.days || []).reduce((ds, d) => ds + (d.items || []).filter(i => i.type === 'workout').length, 0), 0);
          return (
            <div key={p.id} className="regen-card-elevated p-5" data-testid={`program-${p.id}`}>
              <div className="flex items-start justify-between">
                <span className="pill text-[10px]" style={{ background: `${BLUE}1a`, color: BLUE }}>{p.goal || 'Program'}</span>
                <span className="text-[11px] font-mono text-muted-foreground">{p.weeks?.length || 0} wks · {totalWorkouts} sessions</span>
              </div>
              <div className="font-display font-bold text-lg mt-2">{p.name}</div>
              {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
              <div className="flex gap-2 mt-4 items-center">
                <Button size="sm" variant="outline" onClick={() => openBuilder(p)} data-testid={`edit-program-${p.id}`} className="border-[var(--c-hair)]"><Edit2 className="w-3 h-3 mr-1.5" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(p.id)} data-testid={`dup-program-${p.id}`} className="border-[var(--c-hair)]"><Copy className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(p.id)} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                <div className="ml-auto"><TemplateVersionHistory ttype="training-programs" tid={p.id} name={p.name} onRestored={load} /></div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Training Program</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Program Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="prog-name" placeholder="e.g. Fat Loss Beginner" className="bg-transparent border-[var(--c-hair)]" /></div>
                <div>
                  <Label>Goal</Label>
                  <Select value={form.goal} onValueChange={v => setForm({ ...form, goal: v })}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="prog-goal"><SelectValue placeholder="Choose goal" /></SelectTrigger>
                    <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-transparent border-[var(--c-hair)]" rows={2} /></div>

              <div className="space-y-3">
                {form.weeks.map((wk, wi) => (
                  <div key={wi} className="rounded-xl border border-[var(--c-hair)] overflow-hidden" data-testid={`prog-week-${wi}`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--c-film)]">
                      <div className="font-display font-bold text-sm">Week {wk.week_number}</div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => dupWeek(wi)} className="h-7 border-[var(--c-hair)] text-xs" data-testid={`dup-week-${wi}`}><Copy className="w-3 h-3 mr-1" /> Duplicate</Button>
                        <Button size="sm" variant="outline" onClick={() => delWeek(wi)} className="h-7 border-red-500/30 text-red-400 text-xs"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="divide-y divide-[var(--c-hair)]">
                      {wk.days.map((day, di) => (
                        <DayRow key={di} day={day} wi={wi} di={di} workouts={workouts}
                          addItem={addItem} updItem={updItem} delItem={delItem} dupDayToNext={dupDayToNext} clearDay={clearDay} />
                      ))}
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addWeek} data-testid="add-week-btn" className="w-full border-dashed border-[var(--c-hair)] text-muted-foreground"><Plus className="w-4 h-4 mr-2" /> Add Week</Button>
              </div>

              <Button onClick={save} data-testid="save-program-btn" className="w-full font-semibold" style={{ background: BLUE, color: '#081726' }}>Save Program</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DayRow({ day, wi, di, workouts, addItem, updItem, delItem, dupDayToNext, clearDay }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const wname = (id) => workouts.find(w => w.id === id)?.name || 'Workout';
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={`prog-day-${wi}-${di}`}>
      <div className="w-10 pt-1 text-xs font-bold text-muted-foreground uppercase">{DAYS[di]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5">
          {day.items.length === 0 && <span className="text-xs text-muted-foreground/60 py-1">Rest day — no sessions</span>}
          {day.items.map((it, ii) => (
            <span key={ii} className="inline-flex items-center gap-1.5 rounded-lg pl-2 pr-1 py-1 text-xs"
              style={{ background: it.type === 'workout' ? `${BLUE}1a` : it.type === 'rest' ? 'rgba(255,255,255,0.06)' : 'rgba(167,139,250,0.12)', color: it.type === 'workout' ? BLUE : it.type === 'note' ? '#C4B5FD' : '#9FB0C0' }}>
              {it.type === 'workout' && <Dumbbell className="w-3 h-3" />}
              {it.type === 'rest' && <Moon className="w-3 h-3" />}
              {it.type === 'note' && <StickyNote className="w-3 h-3" />}
              {it.type === 'note'
                ? <input value={it.label} onChange={e => updItem(wi, di, ii, 'label', e.target.value)} placeholder="Note / Walk 10k…" className="bg-transparent outline-none w-28 placeholder:text-current/50" />
                : <span>{it.type === 'workout' ? wname(it.workout_id) : 'Rest'}</span>}
              <button onClick={() => delItem(wi, di, ii)} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <div className="relative">
            <button onClick={() => setPickerOpen(o => !o)} data-testid={`add-workout-${wi}-${di}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)]">
              <Plus className="w-3 h-3" /> Workout <ChevronDown className="w-3 h-3" />
            </button>
            {pickerOpen && (
              <div className={`absolute z-50 ${di >= 5 ? 'bottom-full mb-1' : 'mt-1'} w-56 max-h-52 overflow-y-auto rounded-lg border border-[var(--c-hair)] bg-[var(--c-elev)] shadow-xl p-1`}>
                {workouts.length === 0 && <div className="text-[11px] text-muted-foreground p-2">No workouts. Create one in Workout Builder.</div>}
                {workouts.map(w => (
                  <button key={w.id} onClick={() => { addItem(wi, di, { type: 'workout', workout_id: w.id, label: '' }); setPickerOpen(false); }} className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-[var(--c-film)]">{w.name}</button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => addItem(wi, di, { type: 'rest', label: 'Rest' })} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)]">+ Rest</button>
          <button onClick={() => addItem(wi, di, { type: 'note', label: '' })} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)]">+ Note</button>
          {day.items.length > 0 && <>
            <button onClick={() => dupDayToNext(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground"><Copy className="w-3 h-3 inline mr-1" />Copy → next day</button>
            <button onClick={() => clearDay(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground">Clear</button>
          </>}
        </div>
      </div>
    </div>
  );
}
